// src/pages/SalaryPayments/SalaryPaymentEntry.js
import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Button,
  DatePicker,
  Table,
  Typography,
  Space,
  App,
  Select,
  Grid,
  Flex,
} from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {
  fetchSalaryPayments,
  fetchSalaryPaymentCode,
  saveSalaryPayment,
} from "./salaryPaymentThunk";
import {
  fetchEmployees,
  fetchLoadIntoAccs,
  fetchSalaryAccs,
} from "../../store/commonThunks/lookups";
import {
  setPaymentDate,
  setIsEditSalary,
  setSelectedSalaryPayment,
  setRefreshList,
  setPaymentCode,
} from "./salaryPaymentSlice";
import SearchListAutoComplete from "../../components/SearchListAutoComplete";
import InputNumberField from "../../components/Input/InputNumber";
import { useNavigate, useParams } from "react-router-dom";
import { dateTimeFormat, formatToCompanyTimezone, formatToUtc, getAccessType, getDateTimeFromSelectedDate } from "../../lib/functions";
import useMigrationDate from "../../hooks/useMigrationDate";

const { Title } = Typography;
const { useBreakpoint } = Grid;
const { TextArea } = Input;

let months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const SalaryPaymentEntry = ({ authInfo }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const screens = useBreakpoint();
  const { modal } = App.useApp();
  const [form] = Form.useForm();

  const [rows, setRows] = useState([]);

  dayjs.extend(customParseFormat);

  const { paymentDate, paymentCode, selectedSalaryPayment, isEditSalary } =
    useSelector((state) => state.salaryPayments);
  const { employees, salaryAccs, loadIntoAccs } = useSelector((s) => s.lookups);

  const [visibleEmployees, setVisibleEmployees] = useState([]);
  let [access_type, access_type_set] = useState("");

  useEffect(() => {
    const access = getAccessType("salary_payment");
    access_type_set(access);
  }, []);

  useEffect(() => {
    if (employees?.length > 0) {
      const accountantRoles = [
        "ForthERP_Basic_admin",
        "ForthERP_Standard_admin",
        "ForthERP_Pro_admin",
        "PharmaERP_Basic_admin",
        "PharmaERP_Standard_admin",
        "PharmaERP_Pro_admin",
        "PharmaBooks_Basic_admin",
        "PharmaBooks_Standard_admin",
        "PharmaBooks_Pro_admin",
        "ForthBooks_Basic_admin",
        "ForthBooks_Standard_admin",
        "ForthBooks_Pro_admin",
        "HRPayroll_Standard_admin",
        "HRPayroll_Pro_admin",
        "SuperERP_admin",
        "ForthERP_Basic_accountant",
        "ForthERP_Standard_accountant",
        "ForthERP_Pro_accountant",
        "PharmaERP_Basic_accountant",
        "PharmaERP_Standard_accountant",
        "PharmaERP_Pro_accountant",
        "PharmaBooks_Basic_accountant",
        "PharmaBooks_Standard_accountant",
        "PharmaBooks_Pro_accountant",
        "ForthBooks_Basic_accountant",
        "ForthBooks_Standard_accountant",
        "ForthBooks_Pro_accountant",
      ];

      const userRoles = authInfo?.userInfo?.user_role_names
        ? authInfo.userInfo.user_role_names.split(",").map((r) => r.trim())
        : [];

      const isAccountant = userRoles.some((role) =>
        accountantRoles.includes(role)
      );

      if (isAccountant) {
        setVisibleEmployees(employees);
      } else {
        setVisibleEmployees(employees.filter((emp) => emp.hide_salary_details !== 1));
      }
    }
  }, [employees, authInfo]);

  // Load initial data
  useEffect(() => {
    if (!id) dispatch(fetchSalaryPaymentCode());
    dispatch(fetchEmployees());
    dispatch(fetchSalaryAccs());
    dispatch(fetchLoadIntoAccs());
    setRows([
      {
        key: Date.now() + Math.random(),
        employee_id: 0,
        pay_type: "",
        pay_amount: 0,
      }]);
  }, [dispatch]);

  // Edit mode handling
  useEffect(() => {
    if (id && !selectedSalaryPayment) {
      dispatch(fetchSalaryPayments())
        .unwrap()
        .then((list) => {
          const found = list.find((r) => r.pay_id == id);
          if (found) {
            dispatch(setSelectedSalaryPayment(found));
            dispatch(setIsEditSalary(true));
          }
        })
        .catch(() => { });
    }
  }, [id, selectedSalaryPayment, dispatch]);

  // Fill form if editing
  useEffect(() => {
    if (isEditSalary && selectedSalaryPayment) {
      const localDate = formatToCompanyTimezone(selectedSalaryPayment.created_date);
      dispatch(setPaymentCode(selectedSalaryPayment.pay_code));

      const parsedDate = dayjs(localDate, "DD-MM-YYYY HH:mm:ss"); // 👈 force correct parse

      form.setFieldsValue({
        pay_date: parsedDate,
        from_acc_id: selectedSalaryPayment.from_acc_id,
        narration: selectedSalaryPayment.narration,
      });

      dispatch(setPaymentDate(parsedDate.format("YYYY-MM-DD HH:mm:ss")));

      setRows(
        (selectedSalaryPayment.details || []).map((d) => ({
          key: d.pay_d_id || Date.now() + Math.random(),
          employee_id: d.to_acc_id,
          employee_name: d.acc_name,
          employee_value: {
            value: d.acc_name,
            label: d.acc_name,
            key: d.to_acc_id,
            raw: { employee_id: d.to_acc_id, employee_name: d.acc_name },
          },
          pay_type: d.pay_type === "Monthly Payment"
          ? "Monthly Salary"
          : d.pay_type,
          pay_amount: d.pay_amount || 0,
        }))
      );
      
    }
  }, [isEditSalary, selectedSalaryPayment, form, dispatch]);

  //once salary acc arrived  set its form field
  useEffect(() => {
    if (isEditSalary && selectedSalaryPayment) {
      // ✅ In edit mode, use the salary account from the selected payment
      form.setFieldsValue({
        salary_acc_id: selectedSalaryPayment.salary_acc_id,
      });
    } else if (salaryAccs?.length > 0) {
      // ✅ In create mode, use the first available salary account
      form.setFieldsValue({
        salary_acc_id: salaryAccs[0].acc_id,
      });
    }
  }, [salaryAccs, isEditSalary, selectedSalaryPayment, form]);

  // Add/remove rows
  const handleAddRow = () => {
    setRows([
      ...rows,
      {
        key: Date.now() + Math.random(),
        employee_id: 0,
        employee_name: "",
        pay_type: "Basic Salary",
        pay_amount: 0,
      },
    ]);
  };
  const handleRemoveRow = (key) => setRows(rows.filter((r) => r.key !== key));

  const handleCancel = () => {
    dispatch(setSelectedSalaryPayment(null));
    dispatch(setIsEditSalary(false));
    navigate("/hrpayroll/salary-payments");
  };


  // Save
  const onFinish = async (values) => {

    try {
      if (rows.length === 0) {
        modal.error({ title: "Salary payment rows cannot be empty." });
        return;
      }

      for (let r of rows) {
        if (!r.employee_name) {
          modal.error({ title: "Please select Employee before saving." });
          return;
        }
        if (!r.pay_type) {
          modal.error({ title: "Please select Pay Type before saving." });
          return;
        }
        if (r.pay_amount == null || r.pay_amount <= 0) {
          modal.error({ title: "Please enter a valid Amount before saving." });
          return;
        }
      }
      const totalAmount = rows.reduce(
        (s, r) => s + (Number(r.pay_amount) || 0),
        0
      );
      if (totalAmount <= 0) {
        modal.error({
          title: "Total salary payment must be greater than zero.",
        });
        return;
      }

      // convert selected Date to utc date + time
      const utcDate = formatToUtc(paymentDate);

      // ✅ JSON payload (not FormData)
      const payload = {
        payment: {
          pay_code: paymentCode.trim(),
          created_date: utcDate,
          pay_total: totalAmount,
          narration: values.narration || "",
          pay_id: isEditSalary ? selectedSalaryPayment.pay_id : 0,
          action: isEditSalary ? "update" : "create",
          status: isEditSalary ? selectedSalaryPayment.status : "p",
          from_acc_id: values.from_acc_id,
          salary_acc_id: values.salary_acc_id,
          year: values.pay_year,
          month: values.pay_month,
        },
        paymentDetail: rows.map((r) => ({
          to_acc_id: r.employee_id,
          acc_name: r.employee_name || "",
          pay_type: r.pay_type === "Monthly Salary"
          ? "Monthly Payment" : r.pay_type,
          pay_amount: r.pay_amount || 0,
        })),
      };

      const res = await dispatch(saveSalaryPayment(payload)).unwrap();

      modal.success({ title: res.msg || "Salary payment saved successfully" });

      dispatch(setSelectedSalaryPayment(null));
      dispatch(setIsEditSalary(false));
      dispatch(setRefreshList(true));
      form.resetFields();
      setRows([]);
      navigate("/hrpayroll/salary-payments");
    } catch (err) {
      modal.error({ title: err.message || "Failed to save salary payment" });
    }
  };

  // Table columns
  const columns = [
    {
      title: "No.",
      render: (_, __, index) => index + 1,
      width: 50,
    },
    {
      title: "Employee",
      dataIndex: "employee_name",
      render: (_, record, index) => (
        <SearchListAutoComplete
          entityType="employee"
          style={{ width: "100%" }}
          value={record.employee_value || null}
          optionsList={(visibleEmployees || []).map((emp) => ({
            sale_id: emp.employee_id,
            display_text: emp.employee_name,
            raw: emp,
          }))}
          onSelect={(obj) => {
            const newRows = [...rows];
            const employee = obj?.raw;

            newRows[index].employee_id = employee?.employee_id || 0;
            newRows[index].employee_name = employee?.employee_name || "";
            newRows[index].employee_value = {
              value: employee?.employee_name,
              label: employee?.employee_name,
              key: employee?.employee_id,
              raw: employee,
            };

            // ✅ Auto-fill if Monthly Salary
            if (
              newRows[index].pay_type === "Monthly Salary" &&
              employee?.total_monthly_salary
            ) {
              newRows[index].pay_amount = employee.total_monthly_salary;
            }

            setRows(newRows);
          }}
          placeholder="Employee"
          showAddNew={false}
        />
      ),
    },
    {
      title: "Pay Type",
      dataIndex: "pay_type",
      render: (_, record, index) => (
        <Select
          style={{ width: "100%" }}
          popupMatchSelectWidth={false}
          styles={{ popup: { root: { minWidth: 180 } } }}
          value={record.pay_type}
          onChange={(val) => {
            const newRows = [...rows];
            newRows[index].pay_type = val;

            const employee = newRows[index].employee_value?.raw;

            // ✅ Auto-fill if Monthly Salary
            if (val === "Monthly Salary" && employee?.total_monthly_salary) {
              newRows[index].pay_amount = employee.total_monthly_salary;
            }

            setRows(newRows);
          }}
        >
          {[
            "Monthly Salary",
            "Basic Salary",
            "Advance Salary",
            "Overtime",
            "Flight Ticket Reimbursement",
            "Commission",
            "Bonus",
            "Deduction",
            "Conveyance",
            "TA",
            "DA",
            "MA",
          ].map((pt) => (
            <Select.Option key={pt} value={pt}>
              {pt}
            </Select.Option>
          ))}
        </Select>
      ),
    },
    {
      title: "Amount",
      dataIndex: "pay_amount",
      width: screens.md ? undefined : 140,
      render: (_, record, index) => (
        <InputNumberField
          min={0}
          value={record.pay_amount}
          style={{ width: "100%" }}
          onFocus={() => {
            if (record.pay_amount === 0) {
              const newRows = [...rows];
              newRows[index].pay_amount = "";
              setRows(newRows);
            }
          }}
          onChange={(val) => {
            const newRows = [...rows];
            newRows[index].pay_amount = Number(val) || 0;
            setRows(newRows);
          }}
        />
      ),
    },
    {
      title: "Actions",
      render: (_, record) => (
        <Button type="link" danger onClick={() => handleRemoveRow(record.key)} icon={<DeleteOutlined />} /> 
      ),
    },
  ];
  const { disabledDate } = useMigrationDate();
  return (
    <div style={{ padding: 16 }}>
      <Title level={3}>Salary Payment Entry</Title>
      <Form
        form={form}
        layout="horizontal"
        style={{ width: "100%", maxWidth: "1200px" }}
        // variant="filled"
        colon={false}
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 18 }}
        initialValues={{
          pay_date: dayjs(paymentDate),
          pay_year: new Date().getFullYear(),
          pay_month: months[new Date().getMonth()],
          salary_acc_id: salaryAccs?.[0]?.acc_id,
        }}
        onFinish={onFinish}
      >
        <Flex
          gap={!screens.md || !screens.xl ? 10 : 80}
          justify="center"
          style={{
            flexDirection: !screens.md || !screens.xl ? "column" : "row",
            width: "100%",
            marginBottom: "24px",
          }}
        >
          <Flex
            vertical
            gap={10}
            style={{ width: !screens.md || !screens.xl ? "100%" : "40%" }}
          >
            <Form.Item
              name="from_acc_id"
              label="From Account"
              rules={[{ required: true, message: "Select a payment account" }]}
            >
              <Select
                showSearch
                data-test
                placeholder="Select from account"
                data-testid="input-from-account"
                style={{ width: "100%" }}
                options={(loadIntoAccs || []).map((acc) => ({
                  value: acc.acc_id,
                  label: acc.acc_name,
                }))}
              />
            </Form.Item>
            <Form.Item
              name="pay_date"
              label="Entry Date"
              rules={[{ required: true, message: "Please select date" }]}
            >
              <DatePicker
                style={{ width: "100%" }}
                value={paymentDate ? dayjs(paymentDate) : null}
                // onChange={(d) =>
                //   dispatch(setPaymentDate(d ? d.toISOString() : null))
                // }
                onChange={(d) =>
                  dispatch(setPaymentDate(d ? d.format("YYYY-MM-DD HH:mm:ss") : null))
                }

                format={dateTimeFormat}
                data-testid="salary-payment-entry-date"
                disabledDate={disabledDate}
              />
            </Form.Item>
            <Form.Item label="Payment Code">
              <Input value={paymentCode} disabled />
            </Form.Item>
          </Flex>

          <Flex
            vertical
            gap={10}
            style={{ width: !screens.md || !screens.xl ? "100%" : "40%" }}
          >
            <Form.Item
              name="pay_year"
              label="Pay Year"
              rules={[{ required: true, message: "Select year" }]}
            >
              <Select
                style={{ width: "100%" }}
                options={Array.from({ length: 10 }).map((_, idx) => {
                  const year = new Date().getFullYear() - 6 + idx;
                  return { value: year, label: year };
                })}
              />
            </Form.Item>

            <Form.Item
              name="pay_month"
              label="Pay Month"
              rules={[{ required: true, message: "Select month" }]}
            >
              <Select
                style={{ width: "100%" }}
                options={months.map((m) => ({ value: m, label: m }))}
              />
            </Form.Item>
            <Form.Item name="salary_acc_id" label="Salary Account">
              <Select
                showSearch
                placeholder="Select salary account"
                style={{ width: "100%" }}
                options={(salaryAccs || []).map((acc) => ({
                  value: acc.acc_id,
                  label: acc.acc_name,
                }))}
                disabled
              />
            </Form.Item>
          </Flex>
        </Flex>

        <Button
          type="dashed"
          onClick={handleAddRow}
          style={{ marginBottom: 12 }}
        >
          + Add Row
        </Button>

        <Table
          dataSource={rows}
          columns={columns}
          pagination={false}
          rowKey="key"
          bordered
          scroll={{ x: "max-content" }}
          style={{ marginTop: 16, marginBottom: 24 }}
          footer={() => {
            const totalAmount = rows.reduce(
              (s, r) => s + Number(r.pay_amount || 0),
              0
            );
            return (
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <strong>Total Payment: {totalAmount.toFixed(2)}</strong>
              </div>
            );
          }}
        />

        <Form.Item name="narration" label="Description">
          <TextArea rows={3} />
        </Form.Item>

        <Space style={{ marginTop: 16 }}>
          <Button onClick={handleCancel}>Back</Button>
          <Button
            onClick={() => {
              form.resetFields();
              setRows([]);
            }}
          >
            Reset
          </Button>
          {access_type == "WRITE" && <>
            <Button type="primary" htmlType="submit">
              Save
            </Button>
          </>}
        </Space>
      </Form>
    </div>
  );
};

export default SalaryPaymentEntry;
